/**
 * Buffer health + lightweight network observations for analytics plugins.
 */
export function createBufferMonitor(options) {
  options = options || {};
  var sampleIntervalMs = options.sampleIntervalMs || 1000;
  var video = null;
  var timer = null;
  var startupStartedAt = 0;
  var startupDelayMs = null;
  var underrunCount = 0;
  var lastBufferedEnd = 0;
  var wasBuffering = false;
  var estimatedBandwidth = 0;
  var recoveryEvents = [];
  var listeners = [];

  function emit(type, payload) {
    for (var i = 0; i < listeners.length; i++) {
      try {
        listeners[i](type, payload);
      } catch (e) {
        /* ignore */
      }
    }
  }

  function sample() {
    if (!video) {
      return;
    }
    var bufferedEnd = 0;
    var bufferAhead = 0;
    try {
      if (video.buffered && video.buffered.length) {
        bufferedEnd = video.buffered.end(video.buffered.length - 1);
        bufferAhead = Math.max(0, bufferedEnd - (video.currentTime || 0));
      }
    } catch (e) {
      /* ignore */
    }

    // Rough bandwidth estimate from buffer growth (bytes unknown → use seconds * heuristic)
    if (bufferedEnd > lastBufferedEnd && sampleIntervalMs > 0) {
      var gained = bufferedEnd - lastBufferedEnd;
      // Assume ~1 Mbps equivalent per second of HD media as a coarse proxy
      estimatedBandwidth = Math.round((gained / (sampleIntervalMs / 1000)) * 1000000);
    }
    lastBufferedEnd = bufferedEnd;

    var snapshot = {
      bufferedEnd: bufferedEnd,
      bufferAhead: bufferAhead,
      currentTime: video.currentTime || 0,
      estimatedBandwidth: estimatedBandwidth,
      underrunCount: underrunCount,
      startupDelayMs: startupDelayMs,
      networkState: video.networkState,
      readyState: video.readyState,
    };
    emit("sample", snapshot);
  }

  return {
    attach: function (el) {
      video = el;
    },

    markLoadStart: function () {
      startupStartedAt = Date.now();
      startupDelayMs = null;
    },

    markCanPlay: function () {
      if (startupStartedAt && startupDelayMs == null) {
        startupDelayMs = Date.now() - startupStartedAt;
        emit("startup", { startupDelayMs: startupDelayMs });
      }
    },

    markBufferStart: function () {
      wasBuffering = true;
      emit("bufferStart", { at: Date.now() });
    },

    markBufferEnd: function () {
      if (wasBuffering) {
        wasBuffering = false;
        emit("bufferEnd", { at: Date.now() });
      }
    },

    markUnderrun: function () {
      underrunCount += 1;
      emit("underrun", { count: underrunCount, at: Date.now() });
    },

    markRecovery: function (info) {
      var entry = Object.assign({ at: Date.now() }, info || {});
      recoveryEvents = [entry].concat(recoveryEvents).slice(0, 20);
      emit("recovery", entry);
    },

    start: function () {
      this.stop();
      timer = setInterval(sample, sampleIntervalMs);
    },

    stop: function () {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    },

    getSnapshot: function () {
      return {
        startupDelayMs: startupDelayMs,
        underrunCount: underrunCount,
        estimatedBandwidth: estimatedBandwidth,
        lastBufferedEnd: lastBufferedEnd,
        recoveryEvents: recoveryEvents.slice(),
        networkState: video ? video.networkState : 0,
        readyState: video ? video.readyState : 0,
      };
    },

    subscribe: function (fn) {
      listeners.push(fn);
      return function () {
        listeners = listeners.filter(function (f) {
          return f !== fn;
        });
      };
    },

    destroy: function () {
      this.stop();
      video = null;
      listeners = [];
      recoveryEvents = [];
    },
  };
}

export default createBufferMonitor;
